import { useCallback, useEffect, useRef, useState } from 'react';
import { clearCourseOtpCache, getCourseVdoCipherOtp } from '../services/vdocipher';
import { publishAppToast } from '../utils/appToast';
import { mapVdoCipherOtpResponse } from '../utils/vdocipherMappers';
import { isSubscriptionRequiredError } from '../utils/authSession';

export function useCourseVideoPlayer({
  courseId,
  enabled = true,
  canPlay = true,
  onError,
}) {
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const requestIdRef = useRef(0);
  const lastNotifiedErrorRef = useRef(null);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const notifyError = useCallback((message) => {
    if (!message || message === lastNotifiedErrorRef.current) return;
    lastNotifiedErrorRef.current = message;
    publishAppToast(message, 'error');
    window.setTimeout(() => {
      onErrorRef.current?.(message);
    }, 0);
  }, []);

  const applyResponse = useCallback(
    (response, requestId) => {
      if (requestId !== requestIdRef.current) return;

      if (response?.success === false) {
        const message = response?.message || 'Unable to load video';
        if (isSubscriptionRequiredError(message)) {
          setSubscriptionRequired(true);
          setError(null);
          setCredentials(null);
          return;
        }
        setSubscriptionRequired(false);
        setError(message);
        setCredentials(null);
        notifyError(message);
        return;
      }

      const mapped = mapVdoCipherOtpResponse(response);
      if (!mapped.otp || !mapped.playbackInfo) {
        const message = 'Video credentials are unavailable right now.';
        setSubscriptionRequired(false);
        setError(message);
        setCredentials(null);
        notifyError(message);
        return;
      }

      setSubscriptionRequired(false);
      setError(null);
      setCredentials(mapped);
    },
    [notifyError]
  );

  useEffect(() => {
    if (!courseId || !enabled || !canPlay) {
      requestIdRef.current += 1;
      setCredentials(null);
      setError(null);
      setSubscriptionRequired(false);
      setLoading(false);
      lastNotifiedErrorRef.current = null;
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    setSubscriptionRequired(false);
    setCredentials(null);
    lastNotifiedErrorRef.current = null;

    let cancelled = false;

    void getCourseVdoCipherOtp(courseId)
      .then((response) => {
        if (cancelled) return;
        applyResponse(response, requestId);
      })
      .catch((e) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        const message = e?.message || 'Failed to load video';
        if (isSubscriptionRequiredError(message)) {
          setSubscriptionRequired(true);
          setError(null);
        } else {
          setError(message);
          notifyError(message);
        }
      })
      .finally(() => {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [courseId, enabled, canPlay, applyResponse]);

  const reload = useCallback(() => {
    if (!courseId) return;
    clearCourseOtpCache(courseId);
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    setError(null);
    setSubscriptionRequired(false);
    setCredentials(null);
    lastNotifiedErrorRef.current = null;

    void getCourseVdoCipherOtp(courseId)
      .then((response) => applyResponse(response, requestId))
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        const message = e?.message || 'Failed to load video';
        setError(message);
        notifyError(message);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [courseId, applyResponse, notifyError]);

  return {
    credentials,
    loading,
    error,
    subscriptionRequired,
    reload,
  };
}
